import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, ShieldAlert, Copy, Trash2, Clock, Loader2, X } from "lucide-react";

function pretty(ts) { return ts ? new Date(ts).toLocaleString() : "—"; }
function remainingLabel(seconds) {
  if (seconds == null) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

/** Panel for Super Admins to provision + revoke time-limited (24h) Approval
 * Test Administrator accounts. Automatically hides when the feature is
 * disabled (production or DISABLE_DEV_TEST_ADMIN=true) or when the current
 * user is not listed in SUPER_ADMIN_EMAILS. */
export default function DevTestAdminPanel({ onClose }) {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [justCreated, setJustCreated] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data: st } = await api.get("/admin/dev/test-admin/status");
      setStatus(st);
      if (st.enabled && st.is_super_admin) {
        const { data } = await api.get("/admin/dev/test-admin/");
        setRows(data.items || []);
      } else {
        setRows([]);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/dev/test-admin/", { label: label || null });
      setJustCreated(data); setLabel(""); await reload();
      toast.success("Test admin created — copy the one-time password now.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create test admin."); }
    finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke and delete this test admin?")) return;
    try {
      await api.delete(`/admin/dev/test-admin/${id}`);
      toast.success("Revoked");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = (text, msg) => {
    navigator.clipboard.writeText(text);
    toast.success(msg || "Copied");
  };

  if (loading || !status) return null;

  return (
    <div className="email-infra-drawer-backdrop" onClick={onClose}>
      <div className="email-infra-drawer" onClick={e => e.stopPropagation()}
           style={{ maxWidth: 640 }} data-testid="dev-test-admin-panel">
        <button className="email-infra-drawer__close" onClick={onClose}><X size={18} /></button>
        <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase",
                        color: "#F59E0B", fontWeight: 700 }}>
          Development · Approval Workflow Testing
        </div>
        <h2 style={{ margin: "4px 0 8px", fontSize: 22, color: "#0F172A" }}>Approval Test Admins</h2>

        {!status.enabled ? (
          <div style={{ padding: 14, background: "#FEE2E2", color: "#991B1B",
                        borderRadius: 10, marginTop: 8 }} data-testid="dta-disabled-banner">
            <ShieldAlert size={16} style={{ display: "inline", marginRight: 6 }} />
            Feature disabled: {status.reason || "unavailable"}.
          </div>
        ) : !status.is_super_admin ? (
          <div style={{ padding: 14, background: "#FEF3C7", color: "#92400E",
                        borderRadius: 10, marginTop: 8 }} data-testid="dta-not-super">
            Only Super Administrators (env <code>SUPER_ADMIN_EMAILS</code>) can create
            approval test admins.
          </div>
        ) : (
          <>
            <div style={{ background: "#F0F5FF", padding: 12, borderRadius: 10, fontSize: 12,
                            color: "#1E3A8A", marginTop: 8 }}>
              Test admins get the dedicated <code>{status.role}</code> role, are limited to
              <strong> {status.ttl_hours}h</strong>, and can ONLY approve / reject / publish
              content in workflow endpoints. They cannot create users, rotate secrets, or
              modify configuration.
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title"><UserPlus size={14} /> Create test admin</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="text" placeholder="Label (optional, e.g. 'Quarterly approval review')"
                        value={label} onChange={e => setLabel(e.target.value)}
                        style={{ flex: 1, padding: 8, borderRadius: 8, border: "1px solid #CBD5E1", fontSize: 13 }}
                        data-testid="dta-label-input" />
                <button className="email-infra-btn email-infra-btn--primary"
                        onClick={create} disabled={creating} data-testid="dta-create-btn">
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                  Provision 24h admin
                </button>
              </div>
              {justCreated ? (
                <div style={{ marginTop: 12, padding: 12, background: "#FEFCE8",
                                  border: "1px solid #FDE68A", borderRadius: 8, fontSize: 12 }}
                     data-testid="dta-just-created">
                  <div style={{ fontWeight: 700, color: "#92400E", marginBottom: 6 }}>
                    ⚠ One-time credentials — copy now, they will never be shown again.
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                    <strong>Email:</strong>
                    <code style={{ fontSize: 12 }} data-testid="dta-created-email">{justCreated.email}</code>
                    <button onClick={() => copy(justCreated.email, "Email copied")}
                            style={{ background: "transparent", border: 0, cursor: "pointer" }}>
                      <Copy size={12} />
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <strong>Password:</strong>
                    <code style={{ fontSize: 12 }} data-testid="dta-created-password">{justCreated.one_time_password}</code>
                    <button onClick={() => copy(justCreated.one_time_password, "Password copied")}
                            style={{ background: "transparent", border: 0, cursor: "pointer" }}>
                      <Copy size={12} />
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: "#78350F", marginTop: 4 }}>
                    Expires {pretty(justCreated.expires_at)}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title"><Clock size={14} /> Active test admins ({rows.length})</div>
              <table className="email-infra-table">
                <thead>
                  <tr><th>Email</th><th>Label</th><th>Created by</th><th>Expires in</th><th></th></tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={5} style={{ color: "#64748B" }}>None active.</td></tr>
                  ) : rows.map(r => (
                    <tr key={r.id} data-testid={`dta-row-${r.id}`}>
                      <td style={{ fontSize: 11 }}><code>{r.email}</code></td>
                      <td>{r.name}</td>
                      <td style={{ fontSize: 12 }}>{r.created_by}</td>
                      <td>{remainingLabel(r.expires_in_seconds)}</td>
                      <td>
                        <button onClick={() => revoke(r.id)}
                                data-testid={`dta-revoke-${r.id}`}
                                style={{ background: "transparent", border: 0, cursor: "pointer",
                                          color: "#991B1B", padding: 4 }}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
